import { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";

import ManagementAuth from '../services/ManagementAuth';

export interface AppAccount {
  isAuthenticated: boolean;
  isManagement: boolean;
  displayName: string | null;
  loginId: string | null;
  idTokenClaims: Record<string, unknown> | undefined;
  logout: () => void;
}

// Drop-in replacement for useIsAuthenticated()/useAccount() that also
// recognizes a "management" (shared PIN) session alongside MSAL, so every
// page only needs to branch on one source of truth for auth state.
const useAppAccount = (): AppAccount => {
  const isMsalAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const [isManagement, setIsManagement] = useState<boolean>(ManagementAuth.isActive());

  useEffect(() => {
    setIsManagement(ManagementAuth.isActive());
    return ManagementAuth.subscribe(setIsManagement);
  }, []);

  const logout = (): void => {
    if (isManagement) {
      ManagementAuth.logout();
    } else {
      instance.logoutRedirect();
    }
  };

  return {
    isAuthenticated: isMsalAuthenticated || isManagement,
    isManagement,
    displayName: isManagement ? "Management" : account?.name,
    loginId: isManagement ? ManagementAuth.getLoginId() : account?.username,
    idTokenClaims: isManagement ? undefined : account?.idTokenClaims,
    logout,
  };
};

export default useAppAccount;
